import * as React from 'react'
import './ScrollyStory.css'
import useSize, { Size } from './useSize'

const defaultRender = (
  width: number,
  height: number,
  progress: number,
  direction: ScrollDirection
) => (
  <div
    style={{
      width,
      height,
      background: '#999',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }}
  >
    <pre
      style={{
        color: '#fff',
        fontSize: '1rem',
        fontFamily: 'Menlo, monospace'
      }}
    >
      progress: {progress.toFixed(3)}
      <br />
      direction: {direction}
      <br />
      width: {width}px
      <br />
      height: {height}px
    </pre>
  </div>
)

type Props = {
  length?: number
  labels?: {
    content: React.ReactNode
    progress: number
  }[]
  render?: (
    width: number,
    height: number,
    progress: number,
    direction?: ScrollDirection
  ) => JSX.Element
}

type ScrollDirection = 'up' | 'down' | undefined

const ScrollyStory = ({
  width,
  height,
  render = defaultRender,
  length = 4,
  labels = []
}: Props & Size) => {
  const stageHeight = length * height
  const stageRef = React.useRef<HTMLDivElement>(null)
  const [scroll, setScroll] = React.useState<{
    top: number
    dir: ScrollDirection
  }>({
    top: -Number.MAX_VALUE,
    dir: undefined
  })

  const progress = Math.min(1, Math.max(scroll.top, 0) / (stageHeight - height))

  function handleScroll() {
    if (stageRef.current) {
      setScroll(scroll => {
        const nextTop = -stageRef.current!.getBoundingClientRect().top
        return {
          top: nextTop,
          dir: scroll.top > nextTop ? 'up' : 'down'
        }
      })
    }
  }

  React.useLayoutEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return (
    <div
      ref={stageRef}
      style={{
        height: stageHeight,
        width: width,
        position: 'relative'
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          height: stageHeight,
          width: width,
          pointerEvents: 'none',
          zIndex: 100
        }}
      >
        {labels.map(l => (
          <div
            style={{
              position: 'absolute',
              top: l.progress * stageHeight,
              width: '100%'
            }}
          >
            {l.content}
          </div>
        ))}
      </div>
      <div
        style={{
          width,
          height,
          position: 'sticky',
          top: 0
        }}
      >
        {render(width, height, progress, scroll.dir)}
      </div>
    </div>
  )
}

const Wrapper = (props: Props) => {
  const ref = React.useRef(null)
  const size = useSize(ref)
  return (
    <div
      ref={ref}
      style={{
        width: '100%',
        minHeight: '100vh'
      }}
    >
      {size ? <ScrollyStory {...props} {...size} /> : null}
    </div>
  )
}

export default Wrapper
